import React from "react";
import {
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  GridRowSelectionModel,
  GridAggregationModel,
  GridColumnMenu,
  useGridApiRef,
  isAutogeneratedRow,
} from "@mui/x-data-grid-premium";
import { MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { DocumentListItemDTO } from "../../types/DocumentListItemDTO";
import DocumentsToolbar from "./DocumentsToolbar";
import StripedDataGrid from "../common/StripedDataGrid";
import { useAppTranslation } from "../../hooks/useAppTranslation";
import { usePersistedGridState } from "../../hooks/usePersistedGridState";
import {
  applySavedColumnOrder,
  omitGridControlledModels,
} from "../../utils/gridStatePersistence";
import {
  createDocumentColumns,
  OwnerOption,
  PaymentMethodOption,
  DocumentColumnPermissions,
} from "./columns";

type Props = {
  gridStorageKey: string;

  rows: DocumentListItemDTO[];
  loading: boolean;
  rowCount: number;

  paginationModel: GridPaginationModel;
  onPaginationModelChange: (m: GridPaginationModel) => void;

  sortModel: GridSortModel;
  onSortModelChange: (m: GridSortModel) => void;

  filterModel: GridFilterModel;
  onFilterModelChange: (m: GridFilterModel) => void;

  onRowClick: (id: string) => void;

  selectionModel: GridRowSelectionModel;
  onSelectionModelChange: (m: GridRowSelectionModel) => void;

  selectedCount: number;
  onApprove: () => void;
  onReject: () => void;

  onBulkPaymentStatus: () => void;

  onAddInvoice: () => void;
  onFetchFromKsef: () => void;
  ksefLoading: boolean;

  onToggleProcessedExternally: (
    row: DocumentListItemDTO,
    value: boolean
  ) => void;

  onToggleProcessed: (
    row: DocumentListItemDTO,
    value: boolean
  ) => void;

  hasPostedSelected: boolean;

  ownerOptions?: OwnerOption[];
  paymentMethodOptions?: PaymentMethodOption[];
  documentColumnPermissions?: DocumentColumnPermissions;
};

function getAggregationModelFromInitialState(
  initialState: any
): GridAggregationModel {
  const model = initialState?.aggregation?.model;

  if (!model || typeof model !== "object") {
    return {};
  }

  return model;
}

type ClearAggregationMenuItemProps = {
  colDef?: {
    field?: string;
  };
  hideMenu?: (event: React.MouseEvent) => void;
  aggregationModel?: GridAggregationModel;
  onAggregationModelChange?: (model: GridAggregationModel) => void;
};

function ClearAggregationMenuItem(props: ClearAggregationMenuItemProps) {
  const {
    colDef,
    hideMenu,
    aggregationModel = {},
    onAggregationModelChange,
  } = props;

  const field = colDef?.field;

  if (!field || !aggregationModel[field]) {
    return null;
  }

  return (
    <MenuItem
      onClick={(event) => {
        event.stopPropagation();

        const nextModel = { ...aggregationModel };
        delete nextModel[field];

        onAggregationModelChange?.(nextModel);
        hideMenu?.(event);
      }}
    >
      <ListItemIcon>
        <ClearIcon fontSize="small" />
      </ListItemIcon>

      <ListItemText>Usuń agregację</ListItemText>
    </MenuItem>
  );
}

function DocumentsColumnMenu(props: any) {
  const {
    aggregationModel,
    onAggregationModelChange,
    ...columnMenuProps
  } = props;

  return (
    <GridColumnMenu
      {...columnMenuProps}
      slots={{
        columnMenuClearAggregationItem: ClearAggregationMenuItem as any,
      }}
      slotProps={{
        columnMenuClearAggregationItem: {
          displayOrder: 70,
          aggregationModel,
          onAggregationModelChange,
        } as any,
      }}
    />
  );
}

function hasFilterItems(model: GridFilterModel) {
  return (model.items ?? []).length > 0;
}

export default function DocumentsGrid(props: Props) {
  const { t } = useAppTranslation("common");
  const apiRef = useGridApiRef();

  const {
    gridStorageKey,
    rows,
    loading,
    rowCount,
    paginationModel,
    onPaginationModelChange,
    sortModel,
    onSortModelChange,
    filterModel,
    onFilterModelChange,
    onRowClick,
    selectionModel,
    onSelectionModelChange,
    selectedCount,
    onApprove,
    onReject,
    onBulkPaymentStatus,
    onAddInvoice,
    onFetchFromKsef,
    ksefLoading,
    onToggleProcessedExternally,
    onToggleProcessed,
    hasPostedSelected,
    ownerOptions,
    paymentMethodOptions,
    documentColumnPermissions,
  } = props;

  const persistedControlledModels = React.useMemo(
    () => ({
      paginationModel,
      sortModel,
      filterModel,
    }),
    [paginationModel, sortModel, filterModel]
  );

  const { initialState, saveState, scheduleSave } = usePersistedGridState(
    apiRef,
    gridStorageKey,
    {
      controlledModels: persistedControlledModels,
    }
  );

  const dataGridInitialState = React.useMemo(
    () => omitGridControlledModels(initialState),
    [initialState]
  );

  const ignoreInitialModelResetRef = React.useRef(true);
  const gridStorageKeyRef = React.useRef(gridStorageKey);

  if (gridStorageKeyRef.current !== gridStorageKey) {
    gridStorageKeyRef.current = gridStorageKey;
    ignoreInitialModelResetRef.current = true;
  }

  React.useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      ignoreInitialModelResetRef.current = false;
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [gridStorageKey]);

  const runAfterInitialRender = React.useCallback((change: () => void) => {
    if (!ignoreInitialModelResetRef.current) {
      change();
      return;
    }

    window.setTimeout(change, 0);
  }, []);

  const [aggregationModel, setAggregationModel] =
    React.useState<GridAggregationModel>(() =>
      getAggregationModelFromInitialState(initialState)
    );

  const aggregationSaveReadyRef = React.useRef(false);

  React.useEffect(() => {
    aggregationSaveReadyRef.current = false;
    setAggregationModel(getAggregationModelFromInitialState(initialState));
  }, [gridStorageKey, initialState]);

  React.useEffect(() => {
    if (!aggregationSaveReadyRef.current) {
      aggregationSaveReadyRef.current = true;
      return;
    }

    scheduleSave();
  }, [aggregationModel, scheduleSave]);

  const handleAggregationModelChange = React.useCallback(
    (model: GridAggregationModel) => {
      setAggregationModel(model);
    },
    []
  );

  const canMarkProcessedExternally =
    documentColumnPermissions?.canMarkProcessedExternally ?? false;

  const canMarkProcessed =
    documentColumnPermissions?.canMarkProcessed ?? false;

  const onToggleProcessedExternallyRef = React.useRef(
    onToggleProcessedExternally
  );

  React.useEffect(() => {
    onToggleProcessedExternallyRef.current = onToggleProcessedExternally;
  }, [onToggleProcessedExternally]);

  const onToggleProcessedRef = React.useRef(onToggleProcessed);

  React.useEffect(() => {
    onToggleProcessedRef.current = onToggleProcessed;
  }, [onToggleProcessed]);

  const baseDocumentColumns = React.useMemo(
    () =>
      createDocumentColumns(
        t,
        (row, value) => {
          onToggleProcessedExternallyRef.current(row, value);
        },
        (row, value) => {
          onToggleProcessedRef.current(row, value);
        },
        ownerOptions,
        paymentMethodOptions,
        {
          canMarkProcessedExternally,
          canMarkProcessed,
        }
      ),
    [
      t,
      ownerOptions,
      paymentMethodOptions,
      canMarkProcessedExternally,
      canMarkProcessed,
    ]
  );

  const documentColumns = React.useMemo(
    () => applySavedColumnOrder(baseDocumentColumns, initialState),
    [baseDocumentColumns, initialState]
  );

  const isRealDocumentRow = React.useCallback((params: any) => {
    if (!params?.row) return false;
    if (isAutogeneratedRow(params.row)) return false;
    if (params?.rowNode && params.rowNode.type !== "leaf") return false;
    if (!params.row.id) return false;

    return true;
  }, []);

  const handlePaginationModelChange = React.useCallback(
    (model: GridPaginationModel) => {
      if (ignoreInitialModelResetRef.current) {
        return;
      }

      runAfterInitialRender(() => {
        onPaginationModelChange(model);
      });
    },
    [onPaginationModelChange, runAfterInitialRender]
  );

  const handleSortModelChange = React.useCallback(
    (model: GridSortModel) => {
      if (
        ignoreInitialModelResetRef.current &&
        sortModel.length > 0 &&
        model.length === 0
      ) {
        return;
      }

      runAfterInitialRender(() => {
        onSortModelChange(model);
      });
    },
    [onSortModelChange, runAfterInitialRender, sortModel]
  );

  const handleFilterModelChange = React.useCallback(
    (model: GridFilterModel) => {
      if (
        ignoreInitialModelResetRef.current &&
        hasFilterItems(filterModel) &&
        !hasFilterItems(model)
      ) {
        return;
      }

      runAfterInitialRender(() => {
        onFilterModelChange(model);
      });
    },
    [filterModel, onFilterModelChange, runAfterInitialRender]
  );

  const handleClearSelection = React.useCallback(() => {
    onSelectionModelChange({ type: "include", ids: new Set() });
  }, [onSelectionModelChange]);

  const handleRowClick = React.useCallback(
    (params: any) => {
      if (!isRealDocumentRow(params)) {
        return;
      }

      saveState();
      onRowClick(String(params.row.id));
    },
    [isRealDocumentRow, onRowClick, saveState]
  );

  return (
    <div style={{ height: "100%", width: "100%" }}>
      <StripedDataGrid
        key={gridStorageKey}
        apiRef={apiRef}
        initialState={dataGridInitialState}
        sx={{
          height: "100%",
          "& .MuiDataGrid-main": {
            overflow: "hidden",
          },
        }}
        showToolbar
        rows={rows}
        columns={documentColumns}
        getRowId={(r) => r.id}
        loading={loading}
        disableRowSelectionOnClick
        checkboxSelection
        isRowSelectable={isRealDocumentRow}
        rowSelectionModel={selectionModel}
        onRowSelectionModelChange={onSelectionModelChange}
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationModelChange}
        pageSizeOptions={[10, 25, 50, 100]}
        sortingMode="server"
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        filterMode="server"
        filterModel={filterModel}
        onFilterModelChange={handleFilterModelChange}
        aggregationModel={aggregationModel}
        onAggregationModelChange={handleAggregationModelChange}
        aggregationRowsScope="filtered"
        disableColumnPinning
        disablePivoting
        onColumnOrderChange={scheduleSave}
        onColumnWidthChange={scheduleSave}
        onColumnVisibilityModelChange={scheduleSave}
        onDensityChange={scheduleSave}
        onPreferencePanelClose={scheduleSave}
        onRowClick={handleRowClick}
        slots={{
          toolbar: DocumentsToolbar as any,
          columnMenu: DocumentsColumnMenu as any,
        }}
        slotProps={{
          columnMenu: {
            aggregationModel,
            onAggregationModelChange: handleAggregationModelChange,
          } as any,
          toolbar: {
            apiRef,
            title: t("Faktury"),
            total: rowCount,
            selectedCount,
            onApprove,
            onReject,
            onBulkPaymentStatus,
            onClearSelection: handleClearSelection,
            onAddInvoice,
            onFetchFromKsef,
            ksefLoading,
            hasPostedSelected,
          } as any,
        }}
      />
    </div>
  );
}